import { ReactComponent as IconDataElement } from "@/assets/svgs/data-element-grey.svg";
import { ReactComponent as IconDocument } from "@/assets/svgs/document-grey.svg";
import { ReactComponent as IconSuccess } from '@/assets/svgs/packaged-icon-ticked.svg';
import { ReactComponent as IconPrevious } from "@/assets/svgs/previous-arrow.svg";
import Button from "@/components/Button/index";
import { ToastContext } from "@/context/toast/index";
import useModal from "@/hooks/use-modal/index";
import usePage from "@/hooks/use-page/index";
import { NavLink } from "@/lib/router-dom/index.js";
import api from "@/services/api/eclips";
import moment from "moment";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ConfirmDeclineModal from "./confirm-decline-modal.jsx";
import ConfirmSendModal from "./confirm-send-modal.jsx";
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index.jsx";

export function TitleHeader({
	referenceNumber,
	id,
	name,
	badge,
	type,
	receivedReferenceNumber,
	enabled,
	loading,
	handleSendClick,
	handleRequestInformation,
	handleAddReference,
	handleUpdateReference,
	handleUpdatePackage,
	setLoading,
	isRemoved,
	reload,
	request,
	isCustom,
	hasDraft,
	shared,
	isCopy
}) {
	const { t } = useTranslation();
	const { addToastMessage } = useContext(ToastContext);
	const isShared = type !== "SEND";
	const toAddress = isShared ? "/eclip/shared" : "/eclip";
	const { breadcrumbsItems, setBreadcrumbsItems } = usePage({
		title: name,
		breadcrumbs: [
			{
				label: '',
				link: `/`,
			},
			{
				label: 'Package Sharing',
				link: `${type === "SEND" ? "/eclip" : "/eclip/shared"}`
			}
		],
	});

	const {
		visible: isConfirmDeclineModalVisible,
		show: showConfirmDeclineModal,
		hide: hideConfirmDeclineModal,
	} = useModal();

	const {
		visible: visibleConfirmCancelModal,
		show: showConfirmCancelModal,
		hide: hideConfirmCancelModal,
	} = useModal();

	const onDeclineConfirm = async (message) => {
		setLoading(true);
		hideConfirmDeclineModal();
		await api.declineRequest(id, {message});
		await reload();
		addToastMessage("Declined package successfully", "success");
	};

	const cancelUpdate = async () => {
		setLoading(true);

		hideConfirmCancelModal();

		await api.cancelUpdateEclip(id);
		setLoading(false);
		await reload();
	};

	useEffect(() => {
		if (name) {
			setBreadcrumbsItems([
				{
					label: '',
					link: `/`,
				},
				{
					label: 'Package Sharing',
					link: `${type === "SEND" ? "/eclip" : "/eclip/shared"}`
				},
				{
					label: `${name}`,
					link: `/eclip/send/${id}`
				},
			]);
		}
	}, [name, type]);

	const updateReference = (
		<span
			className="title-subheader__subtitle--action"
			onClick={handleUpdateReference}
		>
			{t("Update my reference number")}
		</span>
	);

	const isDeclined = badge === "PACKAGE_DECLINED";
	const isDraft = hasDraft();


	const ActionButtons = () => {
		if(isDeclined) {
			return (
				<div className="eclip-create-upload__declined">
					<span>{t("Declined")}</span>
				</div>
			)
		}
		if(typeof handleSendClick === "function" || isRemoved) {
			return (
				<div className="overview__filter overview__button_wrap">
					<Button
						disabled={!enabled || isRemoved || isCopy}
						submit={handleSendClick}
						loading={loading}
						round
						full
						text={"Send"}
					/>
				</div>
			)
		}

		if(isCopy){
			return (
				<></>
			)
		}

		if(isCustom && isDraft){
			return (
				<>
					<div className="overview__filter overview__button_wrap mr-16">
						<Button
							submit={showConfirmCancelModal}
							loading={loading}
							round
							full
							ghost
							text={"Cancel"}
						/>
					</div>
					<div className="overview__filter overview__button_wrap">
						<Button
							submit={handleUpdatePackage}
							disabled={!enabled || isRemoved}
							loading={loading}
							round
							full
							text={"Update Package"}
						/>
					</div>
				</>
			)
		}

		return (
			<></>
		)
	}


	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />
			<ConfirmDeclineModal
				isVisible={isConfirmDeclineModalVisible}
				cancel={hideConfirmDeclineModal}
				confirm={onDeclineConfirm}
				title={"Decline Package"}
				packageName={name}
				text={`Are you sure want to decline the Package ${name}?`}
			/>

			<ConfirmSendModal
				isVisible={visibleConfirmCancelModal}
				title={`Cancel Changes`}
				text={`Are you sure want to cancel the changes you've made in the Package?`}
				cancel={hideConfirmCancelModal}
				confirm={cancelUpdate}
			/>

			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">{name}</div>
					<div className="title-header__subtitle">
						<div className="title-subheader__subtitle--current">
							{referenceNumber ?? receivedReferenceNumber}
						</div>
						{(referenceNumber && !isDeclined) && updateReference}
						{receivedReferenceNumber && referenceNumber ? (
							<div className="title-subheader__subtitle--received">
								{type === "SEND" ? "Receiver" : "Sender"}{" "}
								Reference No. : {receivedReferenceNumber}
							</div>
						) : (
							(referenceNumber === null && !isDeclined) && (
								<span
									className="title-subheader__subtitle--action"
									onClick={handleAddReference}
								>
									{t("Add my reference number")}
								</span>
							)
						)}
						{ shared && (
							<div className="title-subheader__subtitle-package__shared">
								<IconSuccess />
								<span className="title-subheader__subtitle-package__shared-text">
									{t("Package shared on")}
								</span>
								<span className="title-subheader__subtitle-package__shared-date">
								{moment(new Date(shared)).format(
									"DD/MM/yyyy"
								)}
								</span>
							</div>
						)}
					</div>
				</div>
				<div className="title-header__right">
					{isShared && !isDeclined && !request && !isCopy && (
						<div className="overview__filter overview__button_wrap mr-16">
							<Button
								submit={showConfirmDeclineModal}
								loading={loading}
								round
								full
								ghost
								text={"Decline"}
								disabled={isCopy}
							/>
						</div>
					)}
					{typeof handleRequestInformation === "function" &&
						!isDeclined && !isDraft && (
							<div className="overview__filter overview__button_wrap mr-16">
								<Button
									submit={handleRequestInformation}
									loading={loading}
									round
									disabled={isRemoved || isCopy}
									full
									ghost
									text={"Request for more Information"}
								/>
							</div>
						)
					}
					<ActionButtons />
				</div>
			</div>
		</>
	);
}

export function TitleSubHeader({
	template,
	dataElementsCount,
	attachmentsCount,
	created,
	modified,
	expectedDateOfCompletion
}) {
	const { t } = useTranslation();

	return (
		<>
			<div className="title-subheader eclip-data-element-wrapper--title">
				<div className="title-subheader__left title-subheader__left--flex">
					<div className="title-subheader__left-content--margin">
						<div className="title-subheader__title">
							{t("Template")}
						</div>
						<div className="title-subheader__subtitle">
							{template !== "custom-template" ? template : "Custom Template"}
						</div>
					</div>

					<div className="title-subheader__left-content--margin">
						<div className="title-subheader__title">
							{t("creadtedOn")}
						</div>
						<div className="title-subheader__subtitle">
							{moment(new Date(created)).format(
								"DD/MM/yyyy, hh:mm a"
							)}
						</div>
					</div>

					<div className="title-subheader__left-content--margin">
						<div className="title-subheader__title">
							{t("lastModifiedOn")}
						</div>
						<div className="title-subheader__subtitle">
							{moment(new Date(modified)).format(
								"DD/MM/yyyy, hh:mm a"
							)}
						</div>
					</div>

					<div className="title-subheader__left-content--margin">
						<div className="title-subheader__title">
							{t("expectedCompletionDate")}
						</div>
						<div className="title-subheader__subtitle">
							{moment(new Date(expectedDateOfCompletion)).format(
								"DD/MM/yyyy"
							)}
						</div>
					</div>
				</div>

				<div className="title-subheader__right">
					<div className="title-subheader__subtitle">
						<div className="eclip-data-element-flex">
							<div>
								<IconDataElement />{" "}
								{String(dataElementsCount).padStart(2, "0")}{" "}
							</div>
							<div>
								{" "}
								<IconDocument />{" "}
								{String(attachmentsCount).padStart(2, "0")}
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="divider"></div>
		</>
	);
}
